import TimeCalculator from "./time_calculator";
import Graph from "../graph";
import Info from "./info";
import Legend, { legendColor } from "components/legend";
import { Pie } from "react-chartjs-2";

import { speed as speedFixture } from "../fixtures";
import { humanizedTime } from "utils/date";
import clsx from "clsx";

import * as institutionsApi from "api/institutions";
import * as timePerformanceApi from "api/time_performance";

import { useState, useMemo, useEffect } from "react";
import { useUser } from "hooks/user";
import { useQuery } from "react-query";
import { useMounted } from "hooks/react";

export default function ResolutionTimeGraph(): JSX.Element {
  const [institutionName, setInstitutionName] = useState("ENEM");

  const [enableSpeedQuery, setEnableSpeedQuery] = useState(false);

  const { user } = useUser();

  const mounted = useMounted();

  useEffect(() => {
    setEnableSpeedQuery(mounted && !!user);
  }, [mounted, user]);

  const { data: speedResponse, isLoading: isLoadingSpeed } = useQuery(
    "performance/speed",
    timePerformanceApi.index,
    { enabled: enableSpeedQuery }
  );
  const speedData = speedResponse?.body?.data || speedFixture;

  const { data: institutionsResponse, isLoading: isLoadingInstitutions } =
    useQuery("institutions", institutionsApi.index);
  const institutions = institutionsFromResponse(institutionsResponse);
  const institution = institutions.find(
    (institution) => institution.name === institutionName
  );

  const options = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (tooltipItem) =>
              `${tooltipItem.label}: ${humanizedTime(tooltipItem.parsed, true)}`
          }
        }
      }
    };
  }, []);

  const chartData = useMemo(() => {
    const chartLabels = speedData.subjects.map((subject) => subject.name);
    const data = speedData.subjects.map((subject) =>
      Math.round(subject.averageResolutionTime)
    );
    const backgroundColors = speedData.subjects.map((subject) =>
      legendColor(subject.slug)
    );
    return {
      labels: chartLabels,
      datasets: [
        {
          data: data,
          backgroundColor: backgroundColors,
          borderWidth: 2,
          pointBackgroundColor: "white",
          pointRadius: 4,
          pointHoverRadius: 8,
          pointHitRadius: 16,
          pointHoverBorderWidth: 2,
          pointHoverBackgroundColor: "#fff",
          lineTension: 0.4
        }
      ],
      options
    };
  }, [speedData, options]);

  const PieChart = useMemo(
    () => <Pie data={chartData} options={options} />,
    [chartData, options]
  );

  const shouldRenderPieChart = useMemo(() => {
    return !!speedData.subjects.find((subject) => {
      return Number(subject.questionsCount) > 10;
    });
  }, [speedData]);

  function institutionsFromResponse(response) {
    if (!response?.body?.data) return [];

    return response.body.data.filter(
      (institution) => !!institution.totalQuestions
    );
  }

  return (
    <div>
      <Graph
        title="Velocidade de resolução"
        loading={!mounted || isLoadingSpeed}
        className={clsx({ "filter grayscale": !user })}
      >
        <Info
          label="Velocidade atual"
          value={`${humanizedTime(
            speedData.averageResolutionTime,
            true
          )} por questão`}
        />
        <div className="flex flex-col mt-8 md:flex-row">
          <TimeCalculator
            institutions={institutions}
            institution={institution}
            setInstitutionName={setInstitutionName}
            loading={isLoadingInstitutions}
          />

          <div className="flex flex-col flex-grow items-stretch mt-5 md:mt-0">
            {shouldRenderPieChart && (
              <div className="mx-auto w-48 h-48">{PieChart}</div>
            )}

            <div
              className={clsx(
                "subjects-wrapper h-[300px] relative flex flex-col flex-grow overflow-y-scroll md:ml-5 md:h-auto",
                { "mt-5": shouldRenderPieChart }
              )}
            >
              <div className="subjects absolute left-0 top-0 w-full">
                {speedData.subjects.map((subject) => {
                  return (
                    <div
                      key={subject.slug}
                      className="subject flex justify-between px-6 py-6 w-full bg-white border border-gray-100 rounded-l-lg md:px-8"
                    >
                      <div className="flex items-center">
                        <Legend subjectSlug={subject.slug} />
                        <span className="name inline-block font-semibold">
                          {subject.name}
                        </span>
                      </div>
                      {(Number(subject.questionsCount) >= 10 && (
                        <span className="time text-emphasis-medium">
                          {humanizedTime(subject.averageResolutionTime, true)}
                        </span>
                      )) || (
                        <span className="flex flex-col flex-wrap items-end justify-end text-right text-emphasis-low md:flex-row">
                          <span className="inline-block text-inherit md:mr-1">
                            respostas insuficientes
                          </span>
                          <span className="text-inherit">
                            ({subject.questionsCount} / 10)
                          </span>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Graph>
    </div>
  );
}
